import React, { useState, useCallback, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  deleteSystemCanvasModule,
  deleteMultipleSystemCanvasModules,
  updateSystemCanvasModule,
  duplicateSystemCanvasModule,
} from '../Store/CustomModule.action';
import CustomTooltip from 'Components/Tooltip/CustomTooltip';
import Loader from 'Components/Utility/Loader/Loader';
import ConfirmationModal from 'Components/ModalPopup/ConfirmationModal';
import DuplicateModal from 'Components/ModalPopup/duplicate-modal';
import { shortenStringWithTooltip } from 'Components/helper';
import { SystemCanvasModulesListProps, ModuleCard } from './types';
import SvgUploadModal from '../../../../Components/ModalPopup/svg-upload-modal';
import '../CustomModule.css';
import { AddIcon, CustomModulesIcon, DeleteIcon, DuplicateIcon, EditIcon, HideIcon, InfoIcon, ShowIcon, UploadIcon } from '../../../../bombiix-icon';
import { NewCheckbox } from '../../../../Components/PIM/common/NewCheckbox';
import { Floaty, FloatyButton } from '../../../../Components/Floaty';

/**
 * SystemCanvasModulesList component displays a list of system canvas modules with
 * selection, activation toggle, deletion, and duplication capabilities.
 * @param cards - Array of module cards to display
 * @param loading - Loading state indicator
 * @param selectedIds - Object containing selected card IDs
 * @param onToggleOne - Callback for toggling individual card selection
 * @param onToggleActive - Callback for toggling module active status
 * @param onRefetch - Callback for refetching data after operations
 */
const SystemCanvasModulesList: React.FC<SystemCanvasModulesListProps> = ({ cards, loading, selectedIds, onToggleOne, onToggleActive, onRefetch }) => {
  const { t } = useTranslation(['common', 'custom-module-system-canvas']);
  const history = useHistory();
  const dispatch = useDispatch();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<{ id: string; name: string } | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [moduleToDuplicate, setModuleToDuplicate] = useState<{ id: string; name: string } | null>(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [showSvgUploadModal, setShowSvgUploadModal] = useState(false);
  const [moduleToUpload, setModuleToUpload] = useState<{ id: string } | null>(null);
  const [showMultiDeleteModal, setShowMultiDeleteModal] = useState(false);
  const [modulesToDelete, setModulesToDelete] = useState<Array<{ id: string; name: string }>>([]);

  /**
   * Initiates the deletion process for a module
   * @param moduleId - ID of the module to delete
   * @param moduleName - Name of the module for confirmation display
   */
  const handleDeleteClick = useCallback((moduleId: string, moduleName: string) => {
    setModuleToDelete({ id: moduleId, name: moduleName });
    setShowDeleteModal(true);
  }, []);

  /**
   * Confirms and executes the deletion of a module
   */
  const handleDeleteConfirm = useCallback(async () => {
    if (moduleToDelete) {
      try {
        await dispatch(deleteSystemCanvasModule(moduleToDelete.id, t));
        setShowDeleteModal(false);
        setModuleToDelete(null);
        if (onRefetch) {
          onRefetch();
        }
      } catch (error) {
        setShowDeleteModal(false);
        setModuleToDelete(null);
      }
    }
  }, [moduleToDelete, dispatch, t, onRefetch]);

  /**
   * Cancels the module deletion confirmation
   */
  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
    setModuleToDelete(null);
  }, []);

  /**
   * Initiates the duplication process for a module
   * @param moduleId - ID of the module to duplicate
   * @param moduleName - Name of the module for confirmation display
   */
  const handleDuplicateClick = useCallback((moduleId: string, moduleName: string) => {
    setModuleToDuplicate({ id: moduleId, name: moduleName });
    setShowDuplicateModal(true);
  }, []);

  /**
   * Confirms and executes the duplication of a module with a new name
   * @param newModuleName - Name for the duplicated module
   */
  const handleDuplicateConfirm = useCallback(
    async (newModuleName: string) => {
      if (!moduleToDuplicate) return;

      setDuplicateLoading(true);
      try {
        await dispatch(duplicateSystemCanvasModule(moduleToDuplicate.id, newModuleName, t));
        setShowDuplicateModal(false);
        setModuleToDuplicate(null);
        if (onRefetch) {
          onRefetch();
        }
      } catch (error) {
        console.error('Error duplicating module: ', error);
      } finally {
        setDuplicateLoading(false);
      }
    },
    [moduleToDuplicate, dispatch, t, onRefetch],
  );

  /**
   * Cancels the module duplication confirmation
   */
  const handleDuplicateCancel = useCallback(() => {
    setShowDuplicateModal(false);
    setModuleToDuplicate(null);
  }, []);

  /**
   * Toggles the active status of a module
   * @param moduleId - ID of the module to toggle
   * @param currentIsActive - Current active status of the module
   */
  const handleToggleActive = useCallback(
    async (moduleId: string, currentIsActive: boolean) => {
      if (!onToggleActive) return;

      try {
        const newIsActive = !currentIsActive;
        const response = (await dispatch(updateSystemCanvasModule(moduleId, { isActive: newIsActive }, t))) as unknown as { code?: number } | undefined;
        if (response?.code === 200) {
          onToggleActive(moduleId, newIsActive);
        }
      } catch (error) {
        console.error('Failed to toggle module active status:', error);
      }
    },
    [onToggleActive, dispatch, t],
  );

  const loadingContent = useMemo(() => <Loader />, []);

  const emptyContent = useMemo(
    () => (
      <div className="text-center py-5">
        <CustomModulesIcon size={48} className="text-muted mb-3" />
        <h5>{t('CUSTOM_MODULE.NO_MODULES_FOUND', { ns: 'custom-module-system-canvas' })}</h5>
        <p className="text-muted">{t('CUSTOM_MODULE.CREATE_FIRST_MODULE', { ns: 'custom-module-system-canvas' })}</p>
      </div>
    ),
    [t],
  );

  const editTooltipText = useMemo(() => t('COMMON_LABEL.EDIT'), [t]);
  const duplicateTooltipText = useMemo(() => t('COMMON_LABEL.DUPLICATE'), [t]);
  const deleteTooltipText = useMemo(() => t('COMMON_LABEL.DELETE'), [t]);
  const uploadSvgTooltipText = useMemo(() => t('CUSTOM_MODULE.UPLOAD_SVG', { ns: 'custom-module-system-canvas' }), [t]);
  const createdDateLabel = useMemo(() => t('CUSTOM_MODULE.INFO.CREATED_DATE', { ns: 'custom-module-system-canvas' }), [t]);
  const modifiedDateLabel = useMemo(() => t('CUSTOM_MODULE.INFO.MODIFIED_DATE', { ns: 'custom-module-system-canvas' }), [t]);

  const handleNavigateToAdd = useCallback(() => {
    history.push('/libraries/custom-module/library/system-canvas/add');
  }, [history]);

  const handleSvgUploadClick = useCallback((moduleId: string) => {
    setModuleToUpload({ id: moduleId });
    setShowSvgUploadModal(true);
  }, []);

  const handleSvgUploadCancel = useCallback(() => {
    setShowSvgUploadModal(false);
    setModuleToUpload(null);
  }, []);

  const selectedModuleIds = useMemo(() => {
    return Object.keys(selectedIds).filter((id) => selectedIds[id]);
  }, [selectedIds]);

  const modulesToDeleteList = useMemo(() => {
    if (selectedModuleIds.length === 0) {
      return [];
    }
    return cards.filter((card) => selectedModuleIds.includes(card.id)).map((card) => ({ id: card.id, name: card.title }));
  }, [selectedModuleIds, cards]);

  const handleMultiDeleteClick = useCallback(() => {
    if (modulesToDeleteList.length === 0) {
      return;
    }
    setModulesToDelete(modulesToDeleteList);
    setShowMultiDeleteModal(true);
  }, [modulesToDeleteList]);

  const handleMultiDeleteConfirm = useCallback(async () => {
    if (modulesToDelete.length > 0) {
      try {
        const ids = modulesToDelete.map((module) => module.id);
        await dispatch(deleteMultipleSystemCanvasModules(ids, t));
        setShowMultiDeleteModal(false);
        setModulesToDelete([]);
        if (onRefetch) {
          onRefetch();
        }
      } catch (error) {
        setShowMultiDeleteModal(false);
        setModulesToDelete([]);
      }
    }
  }, [modulesToDelete, dispatch, t, onRefetch]);

  const handleMultiDeleteCancel = useCallback(() => {
    setShowMultiDeleteModal(false);
    setModulesToDelete([]);
  }, []);

  const selectedCount = useMemo(() => {
    return selectedModuleIds.length;
  }, [selectedModuleIds]);

  const hideTooltipText = useMemo(() => t('COMMON.HIDE', { ns: 'custom-module-system-canvas' }), [t]);
  const showTooltipText = useMemo(() => t('COMMON.SHOW', { ns: 'custom-module-system-canvas' }), [t]);

  const handleEditClick = useCallback(
    (moduleId: string) => {
      history.push(`/libraries/custom-module/library/system-canvas/edit/${moduleId}`);
    },
    [history],
  );

  const handleCheckboxChange = useCallback(
    (moduleId: string) => {
      onToggleOne(moduleId);
    },
    [onToggleOne],
  );

  if (loading) {
    return loadingContent;
  }

  const infoAriaLabel = t('CUSTOM_MODULE.SYSTEM_CANVAS.TABLE_ACTIONS.INFORMATION', { ns: 'custom-module-system-canvas' });

  return (
    <>
      {cards.length > 0 ? (
        <div className="cm-card-grid">
          {cards.map((card: ModuleCard) => {
            const isChecked = !!selectedIds[card.id];
            const toggleTooltipText = card.isActive ? hideTooltipText : showTooltipText;
            const toggleAriaLabel = card.isActive ? hideTooltipText : showTooltipText;
            return (
              <div key={card.id} className="cm-card-grid-item item-list">
                <div className="cm-listing-card card h-100">
                  <div className="card-header d-flex align-items-center pr-1">
                    <ul className="card-header-icon cm-header-icon d-flex align-items-center">
                      <li>
                        <NewCheckbox name={`check_${card.id}`} id={`check_${card.id}`} checked={isChecked} onChange={() => handleCheckboxChange(card.id)} />
                      </li>
                      <li className="li-padding-remove">
                        <CustomTooltip tooltipText={editTooltipText}>
                          <button type="button" className="btn btn-action-icon m-0" aria-label={editTooltipText} onClick={() => handleEditClick(card.id)}>
                            <EditIcon size={24} />
                          </button>
                        </CustomTooltip>
                      </li>
                      <li className="li-padding-remove">
                        <CustomTooltip tooltipText={duplicateTooltipText}>
                          <button
                            type="button"
                            className="btn btn-action-icon m-0"
                            aria-label={duplicateTooltipText}
                            onClick={() => handleDuplicateClick(card.id, card.title)}
                          >
                            <DuplicateIcon size={24} />
                          </button>
                        </CustomTooltip>
                      </li>
                      <li className="li-padding-remove">
                        <CustomTooltip tooltipText={deleteTooltipText}>
                          <button
                            type="button"
                            className="btn btn-action-icon m-0"
                            aria-label={deleteTooltipText}
                            onClick={() => handleDeleteClick(card.id, card.title)}
                          >
                            <DeleteIcon size={24} />
                          </button>
                        </CustomTooltip>
                      </li>
                      <li className="li-padding-remove">
                        <CustomTooltip tooltipText={toggleTooltipText}>
                          <button
                            type="button"
                            className="btn btn-action-icon m-0"
                            aria-label={toggleAriaLabel}
                            onClick={() => handleToggleActive(card.id, card.isActive)}
                          >
                            {card.isActive ? <ShowIcon size={24} /> : <HideIcon size={24} />}
                          </button>
                        </CustomTooltip>
                      </li>
                      <li className="li-padding-remove">
                        <CustomTooltip
                          tooltipText={
                            <div className="text-left">
                              <p className="mb-0">
                                <b>{createdDateLabel}:&nbsp;</b>
                                <span>{card.createdAt || 'N/A'}</span>
                              </p>
                              <p className="mb-0">
                                <b>{modifiedDateLabel}:&nbsp;</b>
                                <span>{card.modifiedAt || 'N/A'}</span>
                              </p>
                            </div>
                          }
                        >
                          <button type="button" className="btn btn-action-icon m-0" aria-label={infoAriaLabel}>
                            <InfoIcon size={24} />
                          </button>
                        </CustomTooltip>
                      </li>
                      <li>
                        <CustomTooltip tooltipText={uploadSvgTooltipText}>
                          <button
                            type="button"
                            className="btn btn-action-icon m-0 p-0"
                            aria-label={uploadSvgTooltipText}
                            onClick={() => handleSvgUploadClick(card.id)}
                          >
                            {card.iconUrl ? (
                              <img
                                src={card.iconUrl}
                                alt={card.title}
                                style={{ width: '32px', height: '32px', objectFit: 'contain', display: 'block' }}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const uploadIcon = e.currentTarget.parentElement?.querySelector('svg');
                                  if (uploadIcon) {
                                    uploadIcon.classList.remove('d-none');
                                  }
                                }}
                                onLoad={(e) => {
                                  const uploadIcon = e.currentTarget.parentElement?.querySelector('svg');
                                  if (uploadIcon) {
                                    uploadIcon.classList.add('d-none');
                                  }
                                }}
                              />
                            ) : null}
                            <UploadIcon size={24} className={card.iconUrl ? 'd-none' : ''} />
                          </button>
                        </CustomTooltip>
                      </li>
                    </ul>
                  </div>
                  <div className="card-body cursor-pointer" onClick={() => handleEditClick(card.id)}>
                    <h4 className="title">{shortenStringWithTooltip(card.title, 30)}</h4>
                    <ul className="card-ul">
                      <li className="text-truncate">
                        <strong className="text-nowrap mr-1">{t('COMMON.BRAND', { ns: 'custom-module-system-canvas' })}:</strong>
                        <span>{card.brand}</span>
                      </li>
                      <li className="text-truncate">
                        <strong className="text-nowrap mr-1">{t('COMMON.SUB_BRAND', { ns: 'custom-module-system-canvas' })}:</strong>
                        <span>{card.subBrand}</span>
                      </li>
                      <li className="text-truncate">
                        <strong className="text-nowrap mr-1">{t('COMMON.PRODUCT_TYPE', { ns: 'custom-module-system-canvas' })}:</strong>
                        <span>{card.productType}</span>
                      </li>
                      <li className="text-truncate">
                        <strong className="text-nowrap mr-1">{t('COMMON.CATEGORY_1', { ns: 'custom-module-system-canvas' })}:</strong>
                        <span>{card.category1}</span>
                      </li>
                      <li className="text-truncate">
                        <strong className="text-nowrap mr-1">{t('COMMON.CATEGORY_2', { ns: 'custom-module-system-canvas' })}:</strong>
                        <span>{card.category2}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        emptyContent
      )}

      {showDeleteModal && moduleToDelete && (
        <ConfirmationModal
          show={showDeleteModal}
          title={t('custom-module-system-canvas:DELETE_CONFIRMATION_TITLE')}
          message={t('custom-module-system-canvas:DELETE_CONFIRMATION', {
            name: moduleToDelete.name,
          })}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          confirmLabel={t('COMMON_LABEL.DELETE')}
        />
      )}

      {showDuplicateModal && moduleToDuplicate && (
        <DuplicateModal
          onOpen={showDuplicateModal}
          originalName={moduleToDuplicate.name}
          onConfirm={handleDuplicateConfirm}
          onCancel={handleDuplicateCancel}
          loading={duplicateLoading}
          type="module"
        />
      )}

      {showSvgUploadModal && moduleToUpload && <SvgUploadModal show={showSvgUploadModal} onHide={handleSvgUploadCancel} moduleId={moduleToUpload.id} />}

      {showMultiDeleteModal && modulesToDelete.length > 0 && (
        <ConfirmationModal
          show={showMultiDeleteModal}
          title={t('custom-module-system-canvas:DELETE_CONFIRMATION_TITLE')}
          message={
            modulesToDelete.length === 1 && modulesToDelete[0]
              ? t('custom-module-system-canvas:DELETE_CONFIRMATION', {
                  name: modulesToDelete[0].name,
                })
              : t('custom-module-system-canvas:DELETE_MULTIPLE_CONFIRMATION', {
                  count: modulesToDelete.length,
                })
          }
          onClose={handleMultiDeleteCancel}
          onConfirm={handleMultiDeleteConfirm}
          confirmLabel={t('COMMON_LABEL.DELETE')}
        />
      )}

      <Floaty className="floting-actions">
        <FloatyButton icon={AddIcon} label={t('COMMON.ADD_MODULE', { ns: 'custom-module-system-canvas' })} onClick={handleNavigateToAdd} />
        <FloatyButton icon={DeleteIcon} label={t('FILTER.BIN')} variant="red" onClick={handleMultiDeleteClick} disabled={selectedCount === 0} />
      </Floaty>
    </>
  );
};

export default memo(SystemCanvasModulesList);
